import L from 'leaflet'
import { useEffect, useRef } from 'react'
import type { MapMeta, PublicSpot } from '../lib/types'

type Props = {
  spots: PublicSpot[]
  meta: MapMeta
  mode: 'view' | 'draw' | 'edit'
  selectedSpotId?: string | null
  draftVertices?: [number, number][]
  editVertices?: [number, number][]
  onMoveDraftVertex?: (index: number, lng: number, lat: number) => void
  onMoveVertex?: (index: number, lng: number, lat: number) => void
  onSetDraftVertices?: (vertices: [number, number][]) => void
  onSetEditVertices?: (vertices: [number, number][]) => void
  onInitDraft?: (vertices: [number, number][]) => void
  onSelect?: (spotId: string) => void
  height?: number
  // When true, the map fills its flex parent instead of using a fixed height.
  fill?: boolean
}

// Make `layer` draggable by its body. While dragging, all vertices translate
// together; the handles follow. On mouseup the parent commits via `commit`.
function attachBodyDrag(
  map: L.Map,
  layer: L.Polygon,
  getVertices: () => [number, number][],
  getHandles: () => L.Marker[],
  commit: (vertices: [number, number][]) => void,
) {
  layer.on('mousedown', (downEv: L.LeafletMouseEvent) => {
    L.DomEvent.stopPropagation(downEv.originalEvent)
    L.DomEvent.preventDefault(downEv.originalEvent)
    map.dragging.disable()
    const startLL = downEv.latlng
    const startVertices = getVertices().slice() as [number, number][]
    let lastVertices = startVertices

    const onMove = (mv: L.LeafletMouseEvent) => {
      const dLng = mv.latlng.lng - startLL.lng
      const dLat = mv.latlng.lat - startLL.lat
      lastVertices = startVertices.map(
        ([lng, lat]) => [lng + dLng, lat + dLat] as [number, number],
      )
      layer.setLatLngs(lastVertices.map(([lng, lat]) => [lat, lng] as [number, number]))
      const handles = getHandles()
      lastVertices.forEach(([lng, lat], i) => handles[i]?.setLatLng([lat, lng]))
    }
    const onUp = () => {
      map.off('mousemove', onMove)
      map.off('mouseup', onUp)
      window.removeEventListener('mouseup', onUp)
      map.dragging.enable()
      commit(lastVertices)
    }
    map.on('mousemove', onMove)
    map.on('mouseup', onUp)
    window.addEventListener('mouseup', onUp)
  })
}

// 3m × 5m rectangle centred on the map's current view, in [lng, lat] pairs.
function defaultDraftRect(map: L.Map, widthM = 3, heightM = 5): [number, number][] {
  const c = map.getCenter()
  const dLat = heightM / 111111
  const dLng = widthM / (111111 * Math.cos((c.lat * Math.PI) / 180))
  return [
    [c.lng - dLng / 2, c.lat - dLat / 2],
    [c.lng + dLng / 2, c.lat - dLat / 2],
    [c.lng + dLng / 2, c.lat + dLat / 2],
    [c.lng - dLng / 2, c.lat + dLat / 2],
  ]
}

// Resize a rectangle by dragging corner `k` to `newLL`, keeping the
// diagonally-opposite corner pinned and the rectangle's two edge directions
// (its orientation) intact. Done in screen space so latitude never skews it.
// Assumes `vertices` is a 4-corner ring in order. This is what keeps a
// hand-drawn stand a clean rectangle instead of an arbitrary quadrilateral.
function resizeRectCorner(
  map: L.Map,
  vertices: [number, number][],
  k: number,
  newLL: L.LatLng,
  minPx = 10,
): [number, number][] {
  const opp = (k + 2) % 4
  const nA = (k + 1) % 4
  const nB = (k + 3) % 4
  const toPt = (v: [number, number]) => map.latLngToLayerPoint(L.latLng(v[1], v[0]))
  const oppP = toPt(vertices[opp])
  const aP = toPt(vertices[nA])
  const bP = toPt(vertices[nB])
  const P = map.latLngToLayerPoint(newLL)
  const axA = L.point(aP.x - oppP.x, aP.y - oppP.y)
  const axB = L.point(bP.x - oppP.x, bP.y - oppP.y)
  const lenA = Math.hypot(axA.x, axA.y) || 1
  const lenB = Math.hypot(axB.x, axB.y) || 1
  const uA = L.point(axA.x / lenA, axA.y / lenA)
  const uB = L.point(axB.x / lenB, axB.y / lenB)
  const rel = L.point(P.x - oppP.x, P.y - oppP.y)
  // Project the cursor onto each edge axis; clamp to a min so the box can't
  // collapse or flip through the pinned corner.
  const a = Math.max(rel.x * uA.x + rel.y * uA.y, minPx)
  const b = Math.max(rel.x * uB.x + rel.y * uB.y, minPx)
  const fromPt = (p: L.Point): [number, number] => {
    const ll = map.layerPointToLatLng(p)
    return [ll.lng, ll.lat]
  }
  const out = vertices.slice() as [number, number][]
  out[opp] = vertices[opp]
  out[nA] = fromPt(L.point(oppP.x + a * uA.x, oppP.y + a * uA.y))
  out[nB] = fromPt(L.point(oppP.x + b * uB.x, oppP.y + b * uB.y))
  out[k] = fromPt(L.point(oppP.x + a * uA.x + b * uB.x, oppP.y + a * uA.y + b * uB.y))
  return out
}

const COLORS = {
  free: '#e5ff73',
  parkFree: '#7fb3ff',   // vrij maar auto-vrije zone (Fluweelpark)
  taken: '#5a5a5a',
  selected: '#A077F3',
  draft: '#cf2e2e',
  faded: '#cccccc',
}

const HANDLE_ICON = L.divIcon({
  className: 'edit-vertex-handle',
  html: '<div style="width:18px;height:18px;border-radius:9999px;background:#fff;border:2px solid #000;box-shadow:0 0 0 1px rgba(0,0,0,.2)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
})

const ROTATE_ICON = L.divIcon({
  className: 'edit-rotate-handle',
  html: '<div style="width:22px;height:22px;border-radius:9999px;background:#A077F3;border:2px solid #000;box-shadow:0 0 0 1px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;color:#000;font-size:14px;font-weight:700;line-height:1">↻</div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
})

const ROTATE_HANDLE_OFFSET_PX = 36

function centroidLL(vertices: [number, number][]): L.LatLng {
  let lng = 0, lat = 0
  for (const [x, y] of vertices) { lng += x; lat += y }
  return L.latLng(lat / vertices.length, lng / vertices.length)
}

// Rotate a vertex around `center` in screen space — keeps the polygon shape
// undistorted regardless of latitude. `angle` is in radians, clockwise.
function rotateVertexScreen(
  map: L.Map,
  vertex: L.LatLng,
  center: L.Point,
  cos: number,
  sin: number,
): [number, number] {
  const vp = map.latLngToLayerPoint(vertex)
  const dx = vp.x - center.x
  const dy = vp.y - center.y
  const np = L.point(center.x + dx * cos - dy * sin, center.y + dx * sin + dy * cos)
  const nl = map.layerPointToLatLng(np)
  return [nl.lng, nl.lat]
}

// Wire a rotation handle: drag it around the centroid to rotate the whole
// polygon. `getVertices` is read at drag start; `commit` fires on mouseup.
function attachRotation(
  map: L.Map,
  handle: L.Marker,
  guide: L.Polyline,
  layer: L.Polygon,
  cornerHandles: L.Marker[],
  getVertices: () => [number, number][],
  commit: (vertices: [number, number][]) => void,
) {
  let startVertices: [number, number][] = []
  let centerLL = L.latLng(0, 0)
  let centerPt = L.point(0, 0)
  let startAngle = 0
  let lastVertices: [number, number][] = []

  handle.on('dragstart', () => {
    startVertices = getVertices().slice()
    centerLL = centroidLL(startVertices)
    centerPt = map.latLngToLayerPoint(centerLL)
    const hp = map.latLngToLayerPoint(handle.getLatLng())
    startAngle = Math.atan2(hp.y - centerPt.y, hp.x - centerPt.x)
    lastVertices = startVertices
  })
  handle.on('drag', () => {
    const hp = map.latLngToLayerPoint(handle.getLatLng())
    const angle = Math.atan2(hp.y - centerPt.y, hp.x - centerPt.x) - startAngle
    const cos = Math.cos(angle), sin = Math.sin(angle)
    lastVertices = startVertices.map(([lng, lat]) =>
      rotateVertexScreen(map, L.latLng(lat, lng), centerPt, cos, sin),
    )
    layer.setLatLngs(lastVertices.map(([lng, lat]) => [lat, lng] as [number, number]))
    lastVertices.forEach(([lng, lat], i) => cornerHandles[i]?.setLatLng([lat, lng]))
    guide.setLatLngs([centerLL, handle.getLatLng()])
  })
  handle.on('dragend', () => {
    commit(lastVertices)
  })
}

// Position a rotation handle `offset` pixels above (negative-y in screen
// space) the polygon centroid.
function rotationHandleLatLng(map: L.Map, vertices: [number, number][], offset: number): L.LatLng {
  const c = centroidLL(vertices)
  const cp = map.latLngToLayerPoint(c)
  return map.layerPointToLatLng(L.point(cp.x, cp.y - offset))
}

export default function MapEditor({
  spots,
  meta,
  mode,
  selectedSpotId,
  draftVertices = [],
  editVertices,
  onMoveDraftVertex,
  onMoveVertex,
  onSetDraftVertices,
  onSetEditVertices,
  onInitDraft,
  onSelect,
  height = 600,
  fill = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const polyLayersRef = useRef<Map<string, L.Polygon>>(new Map())
  const draftLayerRef = useRef<L.Polygon | null>(null)
  const draftHandlesRef = useRef<L.Marker[]>([])
  const draftRotateRef = useRef<L.Marker | null>(null)
  const draftRotateGuideRef = useRef<L.Polyline | null>(null)
  const editHandlesRef = useRef<L.Marker[]>([])
  const editLayerRef = useRef<L.Polygon | null>(null)
  const editRotateRef = useRef<L.Marker | null>(null)
  const editRotateGuideRef = useRef<L.Polyline | null>(null)
  const fitDoneRef = useRef(false)

  const onSelectRef = useRef(onSelect)
  const onMoveRef = useRef(onMoveVertex)
  const onMoveDraftRef = useRef(onMoveDraftVertex)
  const onSetDraftRef = useRef(onSetDraftVertices)
  const onSetEditRef = useRef(onSetEditVertices)
  const onInitDraftRef = useRef(onInitDraft)
  onSelectRef.current = onSelect
  onMoveRef.current = onMoveVertex
  onMoveDraftRef.current = onMoveDraftVertex
  onSetDraftRef.current = onSetDraftVertices
  onSetEditRef.current = onSetEditVertices
  onInitDraftRef.current = onInitDraft
  const draftVerticesRef = useRef<[number, number][]>(draftVertices)
  draftVerticesRef.current = draftVertices
  const editVerticesRef = useRef<[number, number][] | undefined>(editVertices)
  editVerticesRef.current = editVertices
  const modeRef = useRef(mode)
  modeRef.current = mode

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      center: [meta.center[1], meta.center[0]],
      zoom: meta.zoom,
      scrollWheelZoom: true,
    })
    const street = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 22,
    })
    const satellite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics',
        maxZoom: 22,
        maxNativeZoom: 19,
      },
    )
    // High-resolution Flemish orthophoto (Digitaal Vlaanderen) — native to
    // zoom 21, far sharper than Esri here. GoogleMapsVL matrixset aligns with
    // Leaflet's standard z/x/y; STYLE must be empty (a value is rejected).
    const ortho = L.tileLayer(
      'https://geo.api.vlaanderen.be/OMWRGBMRVL/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=omwrgbmrvl&STYLE=&FORMAT=image/png&TILEMATRIXSET=GoogleMapsVL&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}',
      {
        attribution: '&copy; Digitaal Vlaanderen &mdash; Orthofoto',
        maxZoom: 22,
        maxNativeZoom: 21,
      },
    )
    ortho.addTo(map)
    L.control.layers(
      { 'Luchtfoto (Vlaanderen)': ortho, 'Satelliet (Esri)': satellite, Plattegrond: street },
      undefined,
      { position: 'topright', collapsed: false },
    ).addTo(map)
    mapRef.current = map
    // Keep Leaflet's internal size in sync when the container is flex-sized
    // (fill mode) and its height changes — e.g. the edit form appearing below.
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(containerRef.current)
    return () => {
      ro.disconnect()
      map.remove()
      mapRef.current = null
      polyLayersRef.current.clear()
      draftLayerRef.current = null
      draftHandlesRef.current = []
      draftRotateRef.current = null
      draftRotateGuideRef.current = null
      editHandlesRef.current = []
      editLayerRef.current = null
      editRotateRef.current = null
      editRotateGuideRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    for (const [id, layer] of polyLayersRef.current) {
      if (!spots.find((s) => s.id === id)) {
        layer.remove()
        polyLayersRef.current.delete(id)
      }
    }

    for (const spot of spots) {
      const isSelected = selectedSpotId === spot.id
      const fadeOthers = mode === 'draw' || (mode === 'edit' && !isSelected)
      const isHiddenForEdit = mode === 'edit' && isSelected
      // Hide the original polygon when it's being edited — the editLayer takes over.
      const fill = isHiddenForEdit
        ? 'transparent'
        : fadeOthers
          ? COLORS.faded
          : isSelected
            ? COLORS.selected
            : spot.taken ? COLORS.taken : spot.carFree ? COLORS.parkFree : COLORS.free
      const opacity = isHiddenForEdit ? 0 : (fadeOthers ? 0.35 : (spot.taken ? 0.55 : 0.85))
      const interactive = mode === 'view' || (mode === 'edit' && !isSelected)
      const latlngs = spot.coordinates.map(([lng, lat]) => [lat, lng] as [number, number])

      let layer = polyLayersRef.current.get(spot.id)
      if (!layer || (layer.options as any).interactive !== interactive) {
        if (layer) { layer.remove(); polyLayersRef.current.delete(spot.id) }
        layer = L.polygon(latlngs, {
          color: '#000000',
          fillColor: fill,
          fillOpacity: opacity,
          weight: fadeOthers || isHiddenForEdit ? 1 : 3,
          interactive,
        }).addTo(map)
        layer.bindTooltip(`${spot.label} — ${spot.streetAddress}`, { sticky: true })
        polyLayersRef.current.set(spot.id, layer)
      } else {
        layer.setLatLngs(latlngs)
        layer.setStyle({ color: '#000000', fillColor: fill, fillOpacity: opacity, weight: fadeOthers || isHiddenForEdit ? 1 : 3 })
      }

      const live = polyLayersRef.current.get(spot.id)!
      live.off('click')
      if (interactive) {
        live.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev)
          onSelectRef.current?.(spot.id)
        })
      }
    }

    if (!fitDoneRef.current && polyLayersRef.current.size > 0) {
      const bounds = L.latLngBounds([])
      for (const layer of polyLayersRef.current.values()) bounds.extend(layer.getBounds())
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: meta.zoom + 1 })
        fitDoneRef.current = true
      }
    }
  }, [spots, mode, selectedSpotId, meta.zoom])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (mode === 'draw' && draftVertices.length === 0) {
      onInitDraftRef.current?.(defaultDraftRect(map))
    }
  }, [mode, draftVertices.length])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (draftLayerRef.current) { draftLayerRef.current.remove(); draftLayerRef.current = null }
    for (const h of draftHandlesRef.current) h.remove()
    draftHandlesRef.current = []
    if (draftRotateRef.current) { draftRotateRef.current.remove(); draftRotateRef.current = null }
    if (draftRotateGuideRef.current) { draftRotateGuideRef.current.remove(); draftRotateGuideRef.current = null }
    if (mode !== 'draw' || draftVertices.length === 0) return

    const latlngs = draftVertices.map(([lng, lat]) => [lat, lng] as [number, number])
    draftLayerRef.current = L.polygon(latlngs, {
      color: '#000', fillColor: COLORS.draft, fillOpacity: 0.4,
      weight: 3, dashArray: '8 6', interactive: true, bubblingMouseEvents: false,
      className: 'cursor-move',
    }).addTo(map)
    attachBodyDrag(
      map,
      draftLayerRef.current,
      () => draftVerticesRef.current,
      () => draftHandlesRef.current,
      (next) => onSetDraftRef.current?.(next),
    )

    // A 4-corner draft is kept rectangular: dragging any corner resizes the
    // box (opposite corner pinned) instead of free-forming a quadrilateral.
    const draftIsRect = draftVertices.length === 4
    const repositionDraftRotate = (verts: [number, number][]) => {
      if (!draftRotateRef.current || !draftRotateGuideRef.current) return
      const hp = rotationHandleLatLng(map, verts, ROTATE_HANDLE_OFFSET_PX)
      draftRotateRef.current.setLatLng(hp)
      draftRotateGuideRef.current.setLatLngs([centroidLL(verts), hp])
    }

    draftVertices.forEach(([lng, lat], i) => {
      const handle = L.marker([lat, lng], { icon: HANDLE_ICON, draggable: true }).addTo(map)
      handle.on('drag', (e: any) => {
        const ll = e.target.getLatLng()
        if (draftIsRect) {
          const next = resizeRectCorner(map, draftVerticesRef.current, i, ll)
          draftLayerRef.current?.setLatLngs(next.map(([lng2, lat2]) => [lat2, lng2] as [number, number]))
          next.forEach(([lng2, lat2], j) => { if (j !== i) draftHandlesRef.current[j]?.setLatLng([lat2, lng2]) })
          repositionDraftRotate(next)
        } else {
          const cur = draftVertices.slice()
          cur[i] = [ll.lng, ll.lat]
          draftLayerRef.current?.setLatLngs(cur.map(([lng2, lat2]) => [lat2, lng2] as [number, number]))
        }
      })
      handle.on('dragend', (e: any) => {
        const ll = e.target.getLatLng()
        if (draftIsRect) {
          onSetDraftRef.current?.(resizeRectCorner(map, draftVerticesRef.current, i, ll))
        } else {
          onMoveDraftRef.current?.(i, ll.lng, ll.lat)
        }
      })
      draftHandlesRef.current.push(handle)
    })

    if (draftVertices.length >= 3) {
      const center = centroidLL(draftVertices)
      const handlePos = rotationHandleLatLng(map, draftVertices, ROTATE_HANDLE_OFFSET_PX)
      draftRotateGuideRef.current = L.polyline([center, handlePos], {
        color: '#000', weight: 1, dashArray: '3 4', interactive: false,
      }).addTo(map)
      draftRotateRef.current = L.marker(handlePos, {
        icon: ROTATE_ICON, draggable: true,
      }).addTo(map)
      attachRotation(
        map,
        draftRotateRef.current,
        draftRotateGuideRef.current,
        draftLayerRef.current,
        draftHandlesRef.current,
        () => draftVerticesRef.current,
        (next) => onSetDraftRef.current?.(next),
      )
    }
  }, [mode, draftVertices])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (editLayerRef.current) { editLayerRef.current.remove(); editLayerRef.current = null }
    for (const h of editHandlesRef.current) h.remove()
    editHandlesRef.current = []
    if (editRotateRef.current) { editRotateRef.current.remove(); editRotateRef.current = null }
    if (editRotateGuideRef.current) { editRotateGuideRef.current.remove(); editRotateGuideRef.current = null }
    if (mode !== 'edit' || !editVertices || editVertices.length === 0) return

    const latlngs = editVertices.map(([lng, lat]) => [lat, lng] as [number, number])
    editLayerRef.current = L.polygon(latlngs, {
      color: '#000', fillColor: COLORS.selected, fillOpacity: 0.55, weight: 3,
      interactive: true, bubblingMouseEvents: false,
      className: 'cursor-move',
    }).addTo(map)
    attachBodyDrag(
      map,
      editLayerRef.current,
      () => editVerticesRef.current ?? [],
      () => editHandlesRef.current,
      (next) => onSetEditRef.current?.(next),
    )

    editVertices.forEach(([lng, lat], i) => {
      const handle = L.marker([lat, lng], { icon: HANDLE_ICON, draggable: true }).addTo(map)
      handle.on('drag', (e: any) => {
        const ll = e.target.getLatLng()
        const cur = editVertices.slice()
        cur[i] = [ll.lng, ll.lat]
        editLayerRef.current?.setLatLngs(cur.map(([lng2, lat2]) => [lat2, lng2] as [number, number]))
      })
      handle.on('dragend', (e: any) => {
        const ll = e.target.getLatLng()
        onMoveRef.current?.(i, ll.lng, ll.lat)
      })
      editHandlesRef.current.push(handle)
    })

    if (editVertices.length >= 3) {
      const center = centroidLL(editVertices)
      const handlePos = rotationHandleLatLng(map, editVertices, ROTATE_HANDLE_OFFSET_PX)
      editRotateGuideRef.current = L.polyline([center, handlePos], {
        color: '#000', weight: 1, dashArray: '3 4', interactive: false,
      }).addTo(map)
      editRotateRef.current = L.marker(handlePos, {
        icon: ROTATE_ICON, draggable: true,
      }).addTo(map)
      attachRotation(
        map,
        editRotateRef.current,
        editRotateGuideRef.current,
        editLayerRef.current,
        editHandlesRef.current,
        () => editVerticesRef.current ?? [],
        (next) => onSetEditRef.current?.(next),
      )
    }
  }, [mode, editVertices])

  return (
    <div
      ref={containerRef}
      className={`rounded-chunky overflow-hidden border border-line${fill ? ' flex-1 min-h-0' : ''}`}
      style={fill ? undefined : { height }}
    />
  )
}
